import { useCallback, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { rescueFosterHomeForContactQuery, type RescueFosterHomeForContactData } from '../lib/rescueQueries';
import { EMAIL_RE, isPhoneComplete } from '../lib/utils';
import type { FosterContactDraft } from '../components/FosterContactFields/FosterContactFields';

/**
 * Whether a typed contact already has a saved foster home, so the two reuse-first writes can say so
 * before one silently refreshes an existing record instead of adding a second.
 *
 * Advisory throughout: quiet on failure, because both callers' writes are correct either way. Only
 * contacts complete enough for the backend to match on are sent, so wiring `check` to a field's
 * `onBlur` doesn't fire a request per tab-through of a half-typed form.
 */
export default function useExistingFosterLookup(draft: FosterContactDraft): {
  /** Display name of the matching home, or null when there's no match to warn about. */
  existingName: string | null;
  check: () => Promise<void>;
  clear: () => void;
} {
  const [existingName, setExistingName] = useState<string | null>(null);
  const [lookupContact] = useLazyQuery<RescueFosterHomeForContactData>(rescueFosterHomeForContactQuery, {
    fetchPolicy: 'network-only',
  });

  const email = draft.email.trim();
  const phone = draft.phone.trim();

  const check = useCallback(async () => {
    const emailValue = email && EMAIL_RE.test(email) ? email : '';
    const phoneValue = isPhoneComplete(phone) ? phone : '';
    if (!emailValue && !phoneValue) {
      setExistingName(null);
      return;
    }
    try {
      const { data } = await lookupContact({
        variables: { email: emailValue || null, phoneNumber: phoneValue || null },
      });
      const match = data?.rescueFosterHomeForContact ?? null;
      setExistingName(match ? [match.contactFirstName, match.contactLastName].filter(Boolean).join(' ') : null);
    } catch {
      setExistingName(null);
    }
  }, [email, phone, lookupContact]);

  const clear = useCallback(() => setExistingName(null), []);

  return { existingName, check, clear };
}
